import { useEffect, useMemo, useState } from 'react';

import { type Environment, fetchQuery, graphql } from 'react-relay';

import { type AGGError, isUnauthenticated } from '@atlassian/relay-errors';

import type { useUnitLinkedResourceIdsQuery } from './__generated__/useUnitLinkedResourceIdsQuery.graphql';

const DEFAULT_PAGE_SIZE = 50;

// Upper bound on pages followed in a single fetch, guarding against a backend
// that never reports `hasNextPage: false`.
const MAX_PAGES = 1000;

// A rejected Relay `fetchQuery` promise carries the GraphQL errors on
// `error.source.errors`.
const getAggErrors = (error: unknown): AGGError[] => {
	const source = (error as { source?: { errors?: unknown } } | null)?.source;
	return Array.isArray(source?.errors) ? (source.errors as AGGError[]) : [];
};

export interface UnitLinkedResourceIdsOptions {
	/**
	 * ATIs (Atlassian Type Identifiers) to restrict the returned links to, e.g.
	 * `['ati:cloud:atlassian-studio:workspace']`. A link is returned if it matches
	 * any one of the supplied ATIs. Omit, or supply an empty list, to return every
	 * target type.
	 */
	atis?: string[];
	/**
	 * Whether the hook may run its query. Defaults to `true`. When `false`, no
	 * request is made and the result settles to an empty, non-loading state; use
	 * this to defer the fetch until the data is actually needed. Toggling to `true`
	 * triggers the fetch.
	 */
	enabled?: boolean;
	/**
	 * Number of links to request per page. Defaults to {@link DEFAULT_PAGE_SIZE}.
	 * The hook always follows the connection cursor until `pageInfo.hasNextPage`
	 * is `false`, so this only controls the network page size, not the final set.
	 */
	pageSize?: number;
}

export interface UnitLinkedResourceIdsResult {
	error: boolean;
	/** All linked-resource ARIs returned for the given anchor resource's unit. */
	linkedResourceIds: string[];
	loading: boolean;
}

/**
 * Fetches the set of resource ARIs linked to the given anchor resource's unit
 * (collaboration context) via the `admin_unitLinkedResource` GraphQL query.
 *
 * The query is server-side permission filtered by CCM, so only links the caller is
 * permitted to see are returned. The `links` connection is paged through
 * automatically: the hook follows `pageInfo.endCursor` until `pageInfo.hasNextPage`
 * is `false`, accumulating every linked-resource ARI.
 *
 * @param anchorResourceAri - The resource ARI to resolve linked unit resources for.
 * When empty, the hook skips the fetch and settles to an empty result.
 * @param options - Optional caller-supplied refinements. See
 * {@link UnitLinkedResourceIdsOptions}.
 */
export const useUnitLinkedResourceIds = (
	relayEnvironment: Environment,
	anchorResourceAri: string,
	options?: UnitLinkedResourceIdsOptions,
): UnitLinkedResourceIdsResult => {
	const atis = options?.atis;
	const enabled = options?.enabled ?? true;
	const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;

	// Content-stable so a fresh `atis` array with identical contents does not
	// re-run the effect below.
	const atisKey = JSON.stringify(atis ?? []);
	const atisFilter = useMemo<string[]>(() => JSON.parse(atisKey), [atisKey]);

	const [state, setState] = useState<UnitLinkedResourceIdsResult>({
		linkedResourceIds: [],
		loading: enabled && Boolean(anchorResourceAri),
		error: false,
	});

	useEffect(() => {
		if (!enabled || !anchorResourceAri) {
			setState({ linkedResourceIds: [], loading: false, error: false });
			return;
		}

		// Closure flag (not a shared ref) so a stale in-flight fetch resolving after
		// a newer effect run cannot overwrite state for a newer ARI / filter.
		let cancelled = false;
		setState({ linkedResourceIds: [], loading: true, error: false });

		const fetchPage = (after: string | null) =>
			fetchQuery<useUnitLinkedResourceIdsQuery>(
				relayEnvironment,
				graphql`
					query useUnitLinkedResourceIdsQuery(
						$anchorAri: ID!
						$after: String
						$first: Int
						$filter: AdminUnitLinkedResourceFilter
					) {
						admin_unitLinkedResource(id: $anchorAri) {
							id
							links(filter: $filter, first: $first, after: $after) {
								edges {
									node {
										id
									}
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
						}
					}
				`,
				{ anchorAri: anchorResourceAri, after, first: pageSize, filter: { atis: atisFilter } },
				{ fetchPolicy: 'store-or-network' },
			).toPromise();

		// Walk the connection cursor until the server reports no further pages,
		// accumulating every linked-resource ARI across pages.
		const fetchAllPages = async (): Promise<string[]> => {
			const ids: string[] = [];
			const seenCursors = new Set<string>();
			let after: string | null = null;
			let hasNextPage = true;

			for (let page = 0; hasNextPage && page < MAX_PAGES; page++) {
				const result = await fetchPage(after);
				const connection = result?.admin_unitLinkedResource?.links;

				for (const edge of connection?.edges ?? []) {
					const id = edge?.node?.id;
					if (typeof id === 'string') {
						ids.push(id);
					}
				}

				const { hasNextPage: next, endCursor } = connection?.pageInfo ?? {};
				// Stop if there is no further page, or the cursor does not advance
				// (missing or repeated) — following it would loop forever.
				hasNextPage = Boolean(next && endCursor && !seenCursors.has(endCursor));
				if (hasNextPage && endCursor) {
					seenCursors.add(endCursor);
					after = endCursor;
				}
			}

			return ids;
		};

		fetchAllPages()
			.then((ids) => {
				if (cancelled) {
					return;
				}
				setState({ linkedResourceIds: ids, loading: false, error: false });
			})
			.catch((err: unknown) => {
				if (cancelled) {
					return;
				}
				// UNAUTHENTICATED is expected on anonymous-reachable routes (the query
				// requires a session), so treat it as a benign empty result, not an error.
				const error = !isUnauthenticated(getAggErrors(err));
				setState({ linkedResourceIds: [], loading: false, error });
			});

		return () => {
			cancelled = true;
		};
	}, [anchorResourceAri, atisFilter, enabled, pageSize, relayEnvironment]);

	return state;
};
